import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Trash2, FolderInput, Loader2 } from "lucide-react";
import { useCad } from "../context/CadContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ProjectsDialog({ open, onOpenChange }) {
  const { serializeProject, applyProject } = useCad();
  const [name, setName] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setList(res.data || []);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => { if (open) refresh(); }, [open]);

  const save = async () => {
    if (!name.trim()) { toast.error("Give the project a name"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/projects`, { name: name.trim(), data: serializeProject() });
      toast.success(`Saved "${name.trim()}"`);
      setName("");
      refresh();
    } catch (e) {
      toast.error("Save failed", { description: String(e.message || e) });
    } finally {
      setLoading(false);
    }
  };

  const load = async (id, pname) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${id}`);
      applyProject(res.data.data);
      toast.success(`Loaded "${pname}"`);
      onOpenChange(false);
    } catch (e) {
      toast.error("Load failed", { description: String(e.message || e) });
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/projects/${id}`);
      refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-md" data-testid="projects-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Projects</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Save the current design (shapes, layers, connectors, settings &amp; imported base) and reopen it later.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input
            data-testid="project-name-input"
            value={name}
            placeholder="Project name…"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            className="h-9 bg-slate-950/60 border-slate-700 text-sm text-slate-100"
          />
          <Button data-testid="project-save-btn" onClick={save} disabled={loading} className="h-9 gap-2 bg-emerald-500 hover:bg-emerald-600 text-white shrink-0">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </Button>
        </div>

        <div className="mt-1 max-h-64 overflow-y-auto cad-scroll space-y-1.5">
          {list.length === 0 && <div className="text-[11px] text-slate-600 py-4 text-center">No saved projects yet.</div>}
          {list.map((p) => (
            <div key={p.id} data-testid={`project-row-${p.id}`} className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-slate-800 bg-slate-950/40">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-200 truncate">{p.name}</div>
                <div className="text-[10px] font-mono text-slate-600">{new Date(p.created_at).toLocaleString()}</div>
              </div>
              <button data-testid={`project-load-${p.id}`} onClick={() => load(p.id, p.name)} className="h-7 px-2 rounded-md bg-slate-800 text-slate-200 hover:bg-emerald-500 hover:text-white text-[11px] flex items-center gap-1 transition-colors duration-150">
                <FolderInput size={13} /> Load
              </button>
              <button data-testid={`project-del-${p.id}`} onClick={() => remove(p.id)} className="h-7 w-7 grid place-items-center rounded-md text-slate-500 hover:text-rose-400">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
